import * as React from "react";
import { css } from "@emotion/core";

type HighlightOptions = {
  highlightColor?: string;
  nonHighlightColor?: string;
  matchTag?: "strong" | "span";
  useWordBoundary?: boolean;
};

const buildSearchTerms = (query: string) =>
  Array.from(
    new Set(
      query
        .trim()
        .split(/\s+/)
        .map((term) => term.toLowerCase())
        .filter(Boolean)
    )
  );

const buildRegex = (terms: string[], useWordBoundary: boolean) => {
  const escapedTerms = terms
    .map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .sort((a, b) => b.length - a.length);

  const boundary = useWordBoundary ? "\\b" : "";
  return new RegExp(`${boundary}(${escapedTerms.join("|")})${boundary}`, "gi");
};

const renderPart = (
  part: string,
  key: React.Key,
  isMatch: boolean,
  options?: HighlightOptions
) => {
  const MatchTag = options?.matchTag === "span" ? "span" : "strong";
  const matchStyle =
    options?.highlightColor &&
    css`
      color: ${options.highlightColor};
    `;
  const nonMatchStyle =
    options?.nonHighlightColor &&
    css`
      color: ${options.nonHighlightColor};
    `;

  if (isMatch) {
    return (
      <MatchTag key={key} css={matchStyle}>
        {part}
      </MatchTag>
    );
  }

  return (
    <span key={key} css={nonMatchStyle}>
      {part}
    </span>
  );
};

export const highlightText = (
  text: string,
  query: string,
  options?: HighlightOptions
): React.ReactNode => {
  if (!text) return "";

  const searchTerms = buildSearchTerms(query);
  const useWordBoundary = options?.useWordBoundary ?? true;

  if (!searchTerms.length) {
    if (!options?.nonHighlightColor && !options?.highlightColor) {
      return text;
    }

    return (
      <span
        css={
          options?.nonHighlightColor
            ? css`
                color: ${options.nonHighlightColor};
              `
            : undefined
        }
      >
        {text}
      </span>
    );
  }

  const regex = buildRegex(searchTerms, useWordBoundary);
  const parts = text.split(regex);

  return parts.map((part, idx) =>
    renderPart(part, `${part}-${idx}`, idx % 2 === 1 && part.length > 0, options)
  );
};

export const extractSnippet = (
  text: string,
  query: string,
  maxLength: number
): React.ReactNode => {
  if (!text) return "";

  const searchTerms = buildSearchTerms(query);
  if (!searchTerms.length) {
    return text.slice(0, maxLength);
  }

  const lowerText = text.toLowerCase();
  let matchIndex = -1;

  searchTerms.forEach((term) => {
    const idx = lowerText.indexOf(term);
    if (idx !== -1 && (matchIndex === -1 || idx < matchIndex)) {
      matchIndex = idx;
    }
  });

  if (matchIndex === -1) {
    return text.slice(0, maxLength);
  }

  const delimiters = new Set([".", "?", "!"]);
  let sentenceStart = 0;

  for (let i = matchIndex - 1; i >= 0; i -= 1) {
    if (delimiters.has(text[i])) {
      sentenceStart = i + 1;
      break;
    }
  }

  while (sentenceStart < text.length && /\s/.test(text[sentenceStart])) {
    sentenceStart += 1;
  }

  let snippetStart = sentenceStart;

  if (matchIndex - snippetStart >= maxLength) {
    snippetStart = Math.max(sentenceStart, matchIndex - Math.floor(maxLength / 2));
  }

  const snippet = text.slice(snippetStart, snippetStart + maxLength);

  return highlightText(snippet, query, {
    highlightColor: "var(--neutral-3)",
    matchTag: "strong",
  });
};

